export type EstadoIntento = "sin_datos" | "iniciada" | "completada" | "fallida" | "descartada";

export interface UltimoIntentoProyeccion {
  estado: EstadoIntento;
  sync_id: string | null;
  observada_en: string | null;
  error_codigo: string | null;
}

export interface ProyeccionActiva {
  sync_id: string | null;
  observada_en: string | null;
  completada_en: string | null;
  version_maxima: number | null;
  generacion: number | null;
}

export interface FrescuraProyeccion {
  modelo: "eventual";
  ultimo_intento: UltimoIntentoProyeccion;
  activa: ProyeccionActiva;
}

export interface FilaBandeja {
  id_nube: string;
  tipo_proceso: string;
  estado: string;
  conteos_json?: string;
  conteos?: Record<string, number>;
  actualizado_en: string;
  manifest_sha256?: string;
  version: number;
}

export interface BandejaExpedientes {
  expedientes: FilaBandeja[];
  proyeccion: FrescuraProyeccion;
}

export interface DetalleExpediente {
  id_nube: string;
  tipo_proceso: string;
  estado: string;
  version: number;
  manifest_sha256: string;
  capacidades: { analizar: boolean; decidir: boolean };
  documentos: { id: string; nombre: string; hash: string; tamano: number }[];
  incidencias: {
    id: string; regla_id: string; severidad: string; detalle: string;
    estado: string; motivo_resolucion: string | null; creada_en: string;
  }[];
  decisiones: { id: string; accion: string; revisor: string; fundamento: string; creada_en: string }[];
  aviso: string;
}

export interface SesionUsuario {
  usuario: string;
  ambiente: string;
  etiqueta_ambiente: string;
  gateway_navegador_url: string;
}

export interface SaludGateway {
  estado: string;
  version: string;
  disco_raiz_libre_gb: number;
  modo_dev: boolean;
  access_configurado: boolean;
}

export interface ArtefactoExpediente {
  artifact_id: string;
  external_name: string;
  sha256: string;
  size_bytes: number;
  tipo: string;
  visible_visor: boolean;
}

export interface ArtefactosExpediente {
  expediente_id: string;
  id_nube: string;
  artefactos: ArtefactoExpediente[];
}

export interface Fragmento {
  id: string;
  processing_id: string;
  tool: string;
  tool_version: string;
  text: string;
  sha256: string;
  bbox: number[] | null;
  kind: string | null;
  confidence: number | null;
}

export interface FragmentosPagina {
  artifact_id: string;
  page_index: number;
  fragments: Fragmento[];
  processings: number;
}

export interface PaginaArtefacto {
  page_index: number;
  mode: "texto" | "ocr";
  width: number;
  height: number;
  fragmentos: number;
}

export interface PaginasArtefacto {
  artifact_id: string;
  external_name: string;
  sha256: string;
  size_bytes: number;
  pages: PaginaArtefacto[];
}

let gatewayBase = "";

// H-L2 (provisional hasta R-02): Bearer token for binary resources. In dev
// mode there is no real identity, so this stays empty and the fetch relies on
// credentials (cookies). When R-02 lands, the login flow sets the token here.
let bearerToken = "";

// R-02 (B3, implemented): short-lived credential obtained from the Worker.
// MEMORY ONLY — never localStorage/sessionStorage: the credential is a
// bearer secret; storage shared across tabs or surviving reloads widens the
// XSS/leak surface for no benefit, since the Worker re-issues it cheaply
// while the Access session is alive.
let credentialExpiraEn = 0;

// H4 (fix adversarial): fail-closed renewal. In production the SPA MUST hold
// a valid credential before calling the Gateway; cookie fallback is allowed
// ONLY in explicit local development. A failed renewal surfaces as a session
// expired error instead of silently sending a stale Bearer.
export let modoDevExplicito = false;

// H4: single-flight renewal — concurrent requests share ONE renewal promise.
let renovacionEnCurso: Promise<void> | null = null;

// N1 (fix adversarial 2026-09-07): monotonic session generation. Every
// logout bumps it; a renewal that started under an older generation must
// NOT reinstall the Bearer when it resolves after cerrarSesion().
let generacionSesion = 0;

// H4: safety margin BEFORE exp so clock skew never sends an expired Bearer.
const MARGEN_VENCIMIENTO_S = 30;

// N2 (fix adversarial 2026-09-07): registry of live Object URLs. Every blob:
// URL created for a downloaded document/raster is registered here;
// cerrarSesion() revokes ALL of them so no downloaded content stays
// reachable after logout. Bounded: when the limit is exceeded the OLDEST
// URL is revoked (FIFO) — the SPA only shows a handful of documents at a
// time, so a small bound is enough and the registry can never grow forever.
const LIMITE_OBJECT_URLS = 64;
const objectUrlsActivos = new Set<string>();

/** N2: register a live Object URL so cerrarSesion() can revoke it later. */
export function registrarObjectUrl(url: string): void {
  objectUrlsActivos.add(url);
  while (objectUrlsActivos.size > LIMITE_OBJECT_URLS) {
    const masAntiguo = objectUrlsActivos.values().next().value as string;
    URL.revokeObjectURL(masAntiguo);
    objectUrlsActivos.delete(masAntiguo);
  }
}

/**
 * N2: unregister an Object URL the consumer has already revoked (or wants
 * to keep managing itself). Safe to call for URLs already revoked.
 */
export function desregistrarObjectUrl(url: string): void {
  objectUrlsActivos.delete(url);
}

export function configurarGateway(base: string, devExplicito = false): void {
  gatewayBase = base.replace(/\/+$/, "");
  modoDevExplicito = devExplicito;
}

/** Provisional (spec T3A-A4): set the Bearer token used for binary fetches. */
export function configurarTokenAutenticacion(token: string): void {
  bearerToken = token;
}

/** Test hook: clear the in-memory credential state between tests. */
export function __resetCredencial(): void {
  bearerToken = "";
  credentialExpiraEn = 0;
  renovacionEnCurso = null;
  generacionSesion += 1;
  modoDevExplicito = false;
}

/**
 * H4 + N1: logout — invalidate the session generation, so any in-flight
 * renewal resolves into a DISCARDED result, clear the credential state and
 * revoke EVERY registered Object URL (N2): downloaded documents/rasters
 * become immediately unreachable after logout.
 */
export function cerrarSesion(): void {
  generacionSesion += 1; // N1: invalidate in-flight renewals
  bearerToken = "";
  credentialExpiraEn = 0;
  renovacionEnCurso = null;
  for (const url of objectUrlsActivos) URL.revokeObjectURL(url); // N2
  objectUrlsActivos.clear();
}

/** H4: session expired signal — the SPA must show this and require re-login. */
export class SesionExpiradaError extends Error {
  constructor(mensaje = "sesión expirada: inicie sesión nuevamente") {
    super(mensaje);
    this.name = "SesionExpiradaError";
  }
}

export interface CredencialGateway {
  token: string;
  expira_en: number;
  ttl_segundos: number;
}

/**
 * R-02 (B3): fetch the short-lived Gateway credential from the Worker.
 * The Worker only issues it behind a verified Access session; the SPA keeps
 * it in memory and re-requests it when expired.
 */
export async function obtenerCredencialGateway(ahora = Date.now(), generacionEsperada: number | null = null): Promise<CredencialGateway> {
  const credencial = await req<CredencialGateway>("/api/v1/credencial-gateway");
  if (!credencial.token || typeof credencial.expira_en !== "number" || credencial.expira_en <= Math.floor(ahora / 1000)) {
    throw new Error("credencial de gateway inválida");
  }
  // N1: never reinstall a Bearer after logout — a renewal that resolved
  // under a stale session generation is discarded.
  if (generacionEsperada !== null && generacionEsperada !== generacionSesion) {
    throw new Error("sesión cerrada durante la renovación");
  }
  configurarTokenAutenticacion(credencial.token);
  credentialExpiraEn = credencial.expira_en;
  return credencial;
}

/** True when the in-memory credential is missing or within the safety margin (re-request). */
export function credencialVencida(ahora = Date.now()): boolean {
  return !bearerToken || credentialExpiraEn - MARGEN_VENCIMIENTO_S <= Math.floor(ahora / 1000);
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(path, init);
  if (!resp.ok) {
    let detalle = "";
    try { detalle = ((await resp.json()) as { detail?: string }).detail ?? ""; } catch { /* vacío */ }
    throw new Error(`HTTP ${resp.status}: ${detalle || resp.statusText}`);
  }
  return (await resp.json()) as T;
}

function rutaGateway(path: string): string {
  if (!gatewayBase) throw new Error("Gateway local no configurado");
  return `${gatewayBase}${path}`;
}

/**
 * H4: single-flight renewal. Concurrent callers share ONE in-flight renewal;
 * a failure clears the credential and (outside explicit dev) throws
 * SesionExpiradaError — the stale Bearer is NEVER sent to the Gateway.
 */
async function asegurarCredencial(): Promise<void> {
  if (!credencialVencida()) return;
  if (!renovacionEnCurso) {
    // N1: capture the session generation; if logout happens while the
    // renewal is in flight, its result is discarded (no Bearer reinstall).
    const generacionAlIniciar = generacionSesion;
    renovacionEnCurso = (async () => {
      try {
        await obtenerCredencialGateway(Date.now(), generacionAlIniciar);
      } catch (error) {
        // H4: fail-closed — clear state; never degrade silently.
        cerrarSesion();
        throw error;
      } finally {
        renovacionEnCurso = null;
      }
    })();
  }
  try {
    await renovacionEnCurso;
  } catch (error) {
    if (modoDevExplicito) return; // explicit local dev: cookie fallback allowed
    throw new SesionExpiradaError();
  }
}

async function reqGateway<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = rutaGateway(path); // fail fast when the Gateway is not configured
  // R-02 (B3) + H4: present the short-lived credential; renew once when
  // expired (single-flight). Fail-closed outside explicit dev.
  await asegurarCredencial();
  const headers = { ...(init.headers as Record<string, string> | undefined) };
  if (bearerToken) headers.authorization = `Bearer ${bearerToken}`;
  return req<T>(url, { ...init, headers, credentials: "include" });
}

/** H-L2 + H5: authenticated fetch for binary resources (raster PNG, docs).
 *
 * Sends credentials plus Authorization: Bearer when a token is configured.
 * The caller owns the blob: URL lifecycle.
 */
export async function fetchBinarioAutenticado(url: string): Promise<Blob> {
  await asegurarCredencial();
  const headers: Record<string, string> = {};
  if (bearerToken) headers.authorization = `Bearer ${bearerToken}`;
  const resp = await fetch(url, { credentials: "include", headers });
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}: ${resp.statusText || "raster no disponible"}`);
  }
  return resp.blob();
}

export const api = {
  sesion: () => req<SesionUsuario>("/api/v1/sesion"),
  bandeja: () => req<BandejaExpedientes>("/api/v1/expedientes"),
  detalle: (id: string) =>
    reqGateway<DetalleExpediente>(`/interno/v1/expedientes/${encodeURIComponent(id)}/detalle`),
  /** H5 + N2: authenticated document download → Blob + registered Object URL
   * (revoked by cerrarSesion or by the consumer via desregistrarObjectUrl). */
  abrirDocumentoAutenticado: async (id: string, nombre: string): Promise<string> => {
    const url = rutaGateway(`/interno/v1/documentos/${encodeURIComponent(id)}/${encodeURIComponent(nombre)}`);
    const blob = await fetchBinarioAutenticado(url);
    const objectUrl = URL.createObjectURL(blob);
    registrarObjectUrl(objectUrl); // N2: revocable on logout
    return objectUrl;
  },
  analizar: (id: string) =>
    reqGateway<{ estado: string }>(`/interno/v1/expedientes/${encodeURIComponent(id)}/analizar`, { method: "POST" }),
  salud: () => reqGateway<SaludGateway>("/interno/v1/salud"),
  decidir: (id: string, body: Record<string, unknown>, idem: string) =>
    reqGateway<{ id: string }>(`/interno/v1/expedientes/${encodeURIComponent(id)}/decisiones`, {
      method: "POST",
      headers: { "content-type": "application/json", "Idempotency-Key": idem },
      body: JSON.stringify(body),
    }),
  paginasArtefacto: (artifactId: string) =>
    reqGateway<PaginasArtefacto>(`/interno/v1/artefactos/${encodeURIComponent(artifactId)}/paginas`),
  artefactosExpediente: (idExpediente: string) =>
    reqGateway<ArtefactosExpediente>(`/interno/v1/expedientes/${encodeURIComponent(idExpediente)}/artefactos`),
  fragmentosPagina: (artifactId: string, pagina: number) =>
    reqGateway<FragmentosPagina>(
      `/interno/v1/artefactos/${encodeURIComponent(artifactId)}/fragmentos?pagina=${pagina}`),
  /** H5: raster fetch stays an internal URL for fetchBinarioAutenticado. */
  rasterPaginaUrl: (artifactId: string, pagina: number) =>
    rutaGateway(`/interno/v1/artefactos/${encodeURIComponent(artifactId)}/paginas/${pagina}/raster.png`),
};
